import { useState } from "react";
import { Input } from "./ui/input";
import { Textarea } from "./ui/textarea";
import { Label } from "./ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "./ui/select";
import { toast } from "sonner";
import { api } from "../lib/api";
import { COUNTRIES, SPECIALTY_OPTIONS } from "../data/site";
import { Check } from "lucide-react";
import FileUploader from "./FileUploader";
import WhatsAppButton from "./WhatsAppButton";
import { waMessageFor } from "../lib/whatsapp";

export default function ConsultationForm({ compact = false }) {
  const [form, setForm] = useState({
    full_name: "", email: "", phone: "", country: "",
    specialty: "", preferred_contact: "email",
    patient_age: "", urgency: "planned", message: "",
  });
  const [files, setFiles] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  const set = (k) => (v) => setForm((f) => ({ ...f, [k]: v?.target ? v.target.value : v }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.full_name || !form.email || !form.phone || !form.country || !form.specialty) {
      toast.error("Please complete the required fields.");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/consultations", { ...form, file_ids: files.map((f) => f.id) });
      setDone(true);
      toast.success("Your request has been received. A concierge will reach you shortly.");
    } catch (err) {
      toast.error("Unable to submit. Please try again or write to concierge@easyilaaz.com.");
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div data-testid="consultation-form-success" className="relative bg-white border border-[#C8A96A]/40 p-10 lg:p-14 text-center">
        <div className="w-14 h-14 rounded-full bg-[#0B1F3A] text-[#C8A96A] mx-auto flex items-center justify-center">
          <Check strokeWidth={1.25} />
        </div>
        <h3 className="mt-6 font-serif text-3xl lg:text-4xl text-[#0B1F3A]">Received in confidence.</h3>
        <p className="mt-4 text-[14px] leading-relaxed text-[#0B1F3A]/70 max-w-md mx-auto">
          A dedicated concierge will personally contact you within one business day.
          For an immediate reply, continue this conversation on WhatsApp — you'll reach
          the same concierge team.
        </p>
        <div className="mt-8 flex justify-center">
          <WhatsAppButton
            variant="primary"
            testId="cf-success-whatsapp"
            message={waMessageFor({ name: form.full_name, specialty: form.specialty, country: form.country, kind: "consultation" })}
          >
            Continue on WhatsApp
          </WhatsAppButton>
        </div>
      </div>
    );
  }

  return (
    <form
      onSubmit={submit}
      data-testid="consultation-form"
      className={`relative bg-white border border-[#E6E9EF] ${compact ? "p-6 lg:p-8" : "p-8 lg:p-12"}`}
    >
      {!compact && (
        <div className="mb-8">
          <div className="overline text-[#C8A96A]">Confidential Consultation</div>
          <h3 className="mt-3 font-serif text-3xl lg:text-4xl text-[#0B1F3A]">Speak with a concierge.</h3>
          <p className="mt-3 text-[14px] text-[#0B1F3A]/70">Shared only with your assigned case manager.</p>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
        <Field label="Full name" required>
          <Input data-testid="cf-name" value={form.full_name} onChange={set("full_name")} placeholder="Your full name" />
        </Field>
        <Field label="Email" required>
          <Input data-testid="cf-email" type="email" value={form.email} onChange={set("email")} placeholder="you@example.com" />
        </Field>
        <Field label="Phone (with country code)" required>
          <Input data-testid="cf-phone" value={form.phone} onChange={set("phone")} placeholder="+1 555 000 0000" />
        </Field>
        <Field label="Country of residence" required>
          <Select value={form.country} onValueChange={set("country")}>
            <SelectTrigger data-testid="cf-country"><SelectValue placeholder="Select country" /></SelectTrigger>
            <SelectContent>
              {COUNTRIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Specialty of interest" required>
          <Select value={form.specialty} onValueChange={set("specialty")}>
            <SelectTrigger data-testid="cf-specialty"><SelectValue placeholder="Select specialty" /></SelectTrigger>
            <SelectContent>
              {SPECIALTY_OPTIONS.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
            </SelectContent>
          </Select>
        </Field>
        <Field label="Preferred contact">
          <Select value={form.preferred_contact} onValueChange={set("preferred_contact")}>
            <SelectTrigger data-testid="cf-contact-pref"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="email">Email</SelectItem>
              <SelectItem value="phone">Phone call</SelectItem>
              <SelectItem value="whatsapp">WhatsApp</SelectItem>
            </SelectContent>
          </Select>
        </Field>
        <Field label="Patient age (optional)">
          <Input data-testid="cf-age" value={form.patient_age} onChange={set("patient_age")} placeholder="e.g. 62" />
        </Field>
        <Field label="Timeline">
          <Select value={form.urgency} onValueChange={set("urgency")}>
            <SelectTrigger data-testid="cf-urgency"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="urgent">Urgent — within days</SelectItem>
              <SelectItem value="soon">Soon — within a month</SelectItem>
              <SelectItem value="planned">Planned — flexible</SelectItem>
            </SelectContent>
          </Select>
        </Field>
      </div>

      <div className="mt-5">
        <Field label="Briefly describe the medical need (optional)">
          <Textarea
            data-testid="cf-message"
            rows={4}
            value={form.message}
            onChange={set("message")}
            placeholder="A short description helps us prepare — e.g., condition, prior treatments, city of preference."
          />
        </Field>
      </div>

      <div className="mt-6">
        <Label className="text-[11px] tracking-[0.16em] uppercase text-[#0B1F3A]/60">
          Medical reports (optional)
        </Label>
        <div className="mt-2">
          <FileUploader files={files} setFiles={setFiles} testId="cf-uploader" />
        </div>
      </div>

      <div className="mt-8 flex flex-col sm:flex-row sm:items-center gap-4 sm:justify-between">
        <p className="text-[11px] leading-relaxed text-[#0B1F3A]/50 max-w-md">
          By submitting you consent to Easyilaaz's private handling of your information.
          We do not share data outside your care team.
        </p>
        <button
          data-testid="cf-submit"
          type="submit"
          disabled={submitting}
          className="inline-flex items-center justify-center h-12 px-8 rounded-full bg-[#0B1F3A] text-white text-[12px] tracking-[0.18em] uppercase hover:bg-[#C8A96A] transition-colors duration-300 disabled:opacity-60"
        >
          {submitting ? "Sending…" : "Request Consultation"}
        </button>
      </div>
    </form>
  );
}

function Field({ label, required, children }) {
  return (
    <div>
      <Label className="text-[11px] tracking-[0.16em] uppercase text-[#0B1F3A]/60">
        {label}{required && <span className="text-[#C8A96A]"> *</span>}
      </Label>
      <div className="mt-2">{children}</div>
    </div>
  );
}
